import React, { useState } from 'react';
import { addCustomCycle } from '../api/Cycles';

interface CreateCustomCycleModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  stateName?: string;
  professionName?: string;
}

const CreateCustomCycleModal: React.FC<CreateCustomCycleModalProps> = ({
  show,
  onHide,
  onSuccess,
  stateName,
  professionName
}) => {
  const [cyclename, setCyclename] = useState('');
  const [cyclestart, setCyclestart] = useState('');
  const [cycleend, setCycleend] = useState('');
  const [customTotalCredits, setCustomTotalCredits] = useState('');
  const [customStateName, setCustomStateName] = useState(stateName || '');
  const [customProfessionName, setCustomProfessionName] = useState(professionName || '');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [upgradeLimit, setUpgradeLimit] = useState<number>(1);
  const [upgradeIsOverride, setUpgradeIsOverride] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!cyclename || !cyclestart || !cycleend || !customTotalCredits || !customStateName || !customProfessionName) {
        throw new Error('Please fill in all required fields');
      }

      const credits = Number(customTotalCredits);
      if (isNaN(credits) || credits <= 0) {
        throw new Error('Total credits must be a positive number');
      }

      await addCustomCycle({
        cyclename,
        cyclestart,
        cycleend,
        customTotalCredits: credits,
        customStateName,
        customProfessionName,
        note
      });

      // Reset form
      setCyclename('');
      setCyclestart('');
      setCycleend('');
      setCustomTotalCredits('');
      setCustomStateName('');
      setCustomProfessionName('');
      setNote('');
      
      onSuccess();
      onHide();
    } catch (err: any) {
      if (err.response?.data?.code === 'FREE_CYCLE_LIMIT') {
        setUpgradeLimit(err.response.data.maxCycles ?? 1);
        setUpgradeIsOverride(err.response.data.isOverride ?? false);
        setError('__UPGRADE__');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to create custom cycle');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    onHide();
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-plus-circle me-2"></i>
              Create Custom Cycle
            </h5>
            <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Custom Cycle:</strong> Use this when official state requirements aren't available in our system yet. 
              We'll be notified and may add official support for this license type.
            </div>

            {error === '__UPGRADE__' ? (
              <div style={{
                background: 'linear-gradient(135deg, #0F1F3D 0%, #1a3560 100%)',
                borderRadius: 10, padding: '1.25rem 1.5rem', color: '#fff', marginBottom: '1rem',
              }}>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 6 }}>
                  {upgradeIsOverride ? '🏅 Founding Member Benefit — Upgrade to Continue' : '🔒 Free plan limit reached'}
                </div>
                <div style={{ opacity: 0.85, fontSize: '0.85rem', marginBottom: '1rem', lineHeight: 1.6 }}>
                  {upgradeIsOverride ? (
                    <>
                      As a founding member, you've been tracking up to{' '}
                      <strong>{upgradeLimit} compliance cycle{upgradeLimit !== 1 ? 's' : ''}</strong> —
                      our thank-you for being with CEC Track from the very beginning.
                      To add a {upgradeLimit + 1}{(['st','nd','rd'])[upgradeLimit] ?? 'th'} cycle or beyond, upgrade to <strong>Pro</strong>.
                    </>
                  ) : (
                    <>Free accounts are limited to one cycle. Upgrade to Pro to create unlimited cycles across any state or profession.</>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.78rem', opacity: 0.75, marginBottom: '1rem' }}>
                  {['Unlimited cycles', 'Multi-state tracking', 'PDF reports', 'Certificate storage'].map(f => (
                    <span key={f}><span style={{ color: '#14b8a6', fontWeight: 700 }}>✓</span> {f}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <a href="/pricing" style={{
                    background: '#14b8a6', color: '#fff', padding: '0.45rem 1.25rem',
                    borderRadius: 7, fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none',
                  }}>
                    Upgrade to Pro →
                  </a>
                  <span style={{ opacity: 0.5, fontSize: '0.78rem' }}>Plans start at $3/mo</span>
                </div>
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : null}

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label">Cycle Name <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g., Vermont Landscape Architect 2024-2026"
                  value={cyclename}
                  onChange={(e) => setCyclename(e.target.value)}
                  required
                />
                <div className="form-text">
                  Give this cycle a descriptive name
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">State <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., Vermont"
                      value={customStateName}
                      onChange={(e) => setCustomStateName(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Profession <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., Landscape Architect"
                      value={customProfessionName}
                      onChange={(e) => setCustomProfessionName(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Cycle Start Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={cyclestart}
                      onChange={(e) => setCyclestart(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Cycle End Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={cycleend}
                      onChange={(e) => setCycleend(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Total Credits Required <span className="text-danger">*</span></label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  className="form-control"
                  placeholder="e.g., 24"
                  value={customTotalCredits}
                  onChange={(e) => setCustomTotalCredits(e.target.value)}
                  required
                />
                <div className="form-text">
                  Total continuing education credits required for this cycle
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Notes (Optional)</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Add any additional details about this cycle..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <div className="alert alert-warning small">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Note:</strong> This will create a custom cycle and notify our admin team at account@cectrack.com. 
                We may add official support for this license type based on your submission.
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Creating...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Create Custom Cycle
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomCycleModal;
